import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { trackPromise } from "react-promise-tracker";
import { sitePagesScreenLogic } from "./logic/sitePagesScreenLogic";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  selectIsLoading,
  selectUserData,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../features/userSlice";
import { WebsitePermissions } from "../../../../data/user/WebsitePermissions";
import { PageGroup } from "../../../../data/sitePages/SitePage";
import { WebsiteWithUuid } from "../../../../data/website/Website";
import { createPageEndPointLogic } from "../../../../logic/AssetManagement/PageManagement/CreatePage/createPageEndPointLogic";
import { sitePageEndPointLogic } from "../../../../logic/AssetManagement/PageManagement/SitePages/sitePagesEndPointLogic";
import store from "../../../../store";
import SimpleBackdrop from "../../../../components/BackDrop/BackDrop";
import SitePages from "../../../../components/AssetManagement/PageBuilder/SitePages/SitePages";
import ErrorPopupAccToPageStatus from "../../../../components/ErrorPopupAccToPageStatus/ErrorPopupAccToPageStatus";
import {
  fetchSitePagesHelper,
  getDateRange,
} from "../PageBuilding/logic/PageBuildingHelpers";
import {
  selectPageActionTrigger,
  selectSitePages,
} from "../../../../features/assetManagementSlice";
import { useDispatch } from "react-redux";

export interface SitePagesScreenProps {
  userToken: string;
  selectedSiteName: string | undefined;
  setPageRefreshStatus: any;
  pageRefreshStatus: boolean;
  response: any;
}

export default function SitePagesScreen({
  selectedSiteName,
  userToken,
  setPageRefreshStatus,
  pageRefreshStatus,
  response,
}: SitePagesScreenProps) {
  // All Capabilities for this screen

  const userWebsitePermissions = useSelector(selectUserData)?.data
    ?.website_permissions as WebsitePermissions | undefined;
  const oauthTokenHeader = userToken;
  const navigate = useNavigate();
  const [createPageResponse, setCreatePageResponse] = useState() as any;
  const [pageContentAfterCreate, setPageContentAfterCreate] = useState() as any;
  const [pageGroups, setPageGroups] = useState<PageGroup[] | undefined>();
  const [openCreateNewPage, setOpenCreateNewPage] = useState<boolean>(false);
  const [errorAccToPageStatus, setErrorAccToPageStatus] = useState([]);
  const [errorAccToPageStatusLength, setErrorAccToPageStatusLength] =
    useState(false);
  const [retry, setRetry] = useState(false);
  const [open, setOpen] = useState(true);
  const [, setPageStructureDesign] = useState();
  const [, setPageContent] = useState();
  const [, setPageDictionary] = useState();
  const [duplicatePageResponse, setDuplicatePageResponse] = useState(null);
  const [deletePageData, setDeletePageData] = useState<string | null>(null);
  const [selectedOptionGroup, setSelectedOptionGroup] = useState([]) as any; // Selected option for group
  const [selectedOptionEdit, setSelectedOptionEdit] = useState<string>(""); // Selected option for edit
  const [pageGroupForAssign, setPageGroupForAssign] = useState([]);
  const [setSelectedPageForAssign] = useState([]);
  const [pageDetails, setPageDetails] = useState([]);
  const [isMetaUpdated, setIsMetaUpdated] = useState(false);
  const pageUid = "";
  const [searchParams] = useSearchParams();
  const pageActionTrigger = useSelector(selectPageActionTrigger);
  const dispatch = useDispatch();
  const selectedSiteUid = searchParams.get("selectedSite") as any;
  const selectedPageUid = pageContentAfterCreate?.shared_uid as any;
  const { yesterday, thirtyDaysAgo } = getDateRange();
  const execute_query_id = "5703829630746624" as string;
  const [queryResults, setQueryResults] = useState<any[]>([]);
  const [mergedSitePages, setMergedSitePages] = useState<any[]>([]);

  const selectedSite = useMemo(() => {
    return (
      (userWebsitePermissions?.find(
        (website) => Object.keys(website)[0] === selectedSiteName
      ) as WebsiteWithUuid) ?? null
    );
    // eslint-disable-next-line
  }, [selectedSiteName]);

  const siteUid = selectedSite && Object.keys(selectedSite)[0];
  const sitePages = useSelector(selectSitePages);
  const isLoading = useSelector(selectIsLoading);
  useEffect(() => {
    if (createPageResponse) {
      createPageEndPointLogic.getPageContentAfterCreate(
        selectedSiteUid,
        createPageResponse?.shared_uid,
        oauthTokenHeader,
        setPageContentAfterCreate,
        setIsLoading
      );
    }
    // eslint-disable-next-line
  }, [createPageResponse]);

  useEffect(() => {
    if (oauthTokenHeader && selectedSiteUid) {
      fetchSitePagesHelper({
        oauthTokenHeader,
        selectedSiteUid,
        thirtyDaysAgo,
        yesterday,
        execute_query_id,
        dispatch,
        setOpen,
        setErrorAccToPageStatus,
        setRetry,
        setIsLoading,
        setQueryResults,
        setPageRefreshStatus,
        setIsMetaUpdated,
        sitePageEndPointLogic,
      });
    }
    // eslint-disable-next-line
  }, [
    selectedSite,
    retry,
    pageActionTrigger,
    createPageResponse,
    pageRefreshStatus,
    isMetaUpdated,
  ]);

  useEffect(() => {
    if (pageContentAfterCreate) {
      sitePagesScreenLogic.navigateToPageBuilding(
        navigate,
        selectedSiteUid,
        createPageResponse,
        pageContentAfterCreate,
        selectedPageUid
      );
    }
    // eslint-disable-next-line
  }, [pageContentAfterCreate]);

  useEffect(() => {
    if (pageGroups && pageGroups?.length > 0) {
      setOpenCreateNewPage(true);
    }
  }, [pageGroups]);

  const handelCreatePagePopUp = () => {
    if (oauthTokenHeader) {
      sitePageEndPointLogic.getPageGroups(
        selectedSiteUid,
        oauthTokenHeader,
        setPageGroups,
        setIsLoading
      );
    } else {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "User Cant Create pages",
          type: "error",
        })
      );
    }
  };
  const pageGroupApiCall = () => {
    if (oauthTokenHeader) {
      sitePageEndPointLogic.getPageGroups(
        selectedSiteUid,
        oauthTokenHeader,
        setPageGroupForAssign,
        setIsLoading
      );
    } else {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "User Cant Create pages",
          type: "error",
        })
      );
    }
  };

  const pageSharedUids = pageDetails.map((item: any) => item?.shared_uid);
  const assignPageGroup = async (data: any) => {
    try {
      const responses = await Promise.all(
        pageSharedUids?.map(async (pageSharedUid) => {
          const response = (await sitePageEndPointLogic.updatePageGroup(
            data,
            Object.keys(selectedSite)[0],
            pageSharedUid,
            oauthTokenHeader
          )) as any;

          if (!response) {
            throw new Error(`Failed to fetch for UID: ${pageSharedUid}`);
          }

          return response;
        }) || [] // fallback if pageSharedUids is undefined
      );

      return responses;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    } finally {
      fetchSitePagesHelper({
        oauthTokenHeader,
        selectedSiteUid,
        thirtyDaysAgo,
        yesterday,
        execute_query_id,
        dispatch,
        setOpen,
        setErrorAccToPageStatus,
        setRetry,
        setIsLoading,
        setQueryResults,
        setPageRefreshStatus,
        setIsMetaUpdated,
        sitePageEndPointLogic,
      });
    }
  };

  const handleCreatePage = (
    pageTitle: string,
    pageName: string,
    pageGroup: string,
    setShowErrorDialog: any
  ) => {
    sitePagesScreenLogic.handleCreatePage(
      pageTitle,
      pageName,
      pageGroup,
      setIsLoading,
      setCreatePageResponse,
      selectedSite,
      oauthTokenHeader,
      setShowErrorDialog
    );
  };

  // Add this function to merge the data
  const mergePageData = (sitePages: any[], queryResults: any[]) => {
    // Group query results by page_shared_uid
    const queryResultsMap = queryResults.reduce((acc, query) => {
      const sharedUid = query.page_shared_uid;
      if (!acc[sharedUid]) {
        acc[sharedUid] = [];
      }
      acc[sharedUid].push(query);
      return acc;
    }, {} as Record<string, any[]>);

    // Merge site pages with query results
    const merged = sitePages.map((page) => {
      const matchingQueries = queryResultsMap[page.shared_uid] || [];

      if (matchingQueries.length === 0) {
        // No matching queries, return page as is
        return {
          ...page,
          queryData: null,
        };
      }

      // If there are matching queries, merge the data
      // For arrays of values, we'll collect all unique values
      const mergedQueryData = {
        keyword_ids: [
          ...new Set(matchingQueries.map((q: any) => q.keyword_id)),
        ],
        adgroup_ids: [
          ...new Set(matchingQueries.map((q: any) => q.adgroup_id)),
        ],
        matchtypes: [...new Set(matchingQueries.map((q: any) => q.matchtype))],
        page_ids: [...new Set(matchingQueries.map((q: any) => q.page_id))],
        current_qs_values: [
          ...new Set(matchingQueries.map((q: any) => q.current_qs)),
        ],
        current_lp_exp_values: [
          ...new Set(matchingQueries.map((q: any) => q.current_lp_exp)),
        ],
        adgroup_names: [
          ...new Set(matchingQueries.map((q: any) => q.adgroup_name)),
        ],
        keyword_names: [
          ...new Set(matchingQueries.map((q: any) => q.keyword_name)),
        ],
        conversion_pages: [
          ...new Set(matchingQueries.map((q: any) => q.conversion_page)),
        ],
        // For numeric values, we might want to sum them
        total_click_in: matchingQueries.reduce(
          (sum: any, q: any) => sum + (q.click_in || 0),
          0
        ),
        total_click_out: matchingQueries.reduce(
          (sum: any, q: any) => sum + (q.click_out || 0),
          0
        ),
        total_ftd: matchingQueries.reduce(
          (sum: any, q: any) => sum + (q.ftd || 0),
          0
        ),
        keyword_count: matchingQueries.length,
        rawQueries: matchingQueries,
      };

      return {
        ...page,
        queryData: mergedQueryData,
      };
    });

    return merged;
  };

  useEffect(() => {
    if (sitePages && queryResults) {
      const merged = mergePageData(sitePages, queryResults);
      setMergedSitePages(merged);
    }
  }, [sitePages, queryResults]);

  useEffect(() => {
    if (errorAccToPageStatus.length === 0) {
      setErrorAccToPageStatusLength(false);
    } else {
      setErrorAccToPageStatusLength(true);
    }
  }, [errorAccToPageStatus]);

  const sitePagesProps = {
    sitePages: mergedSitePages,
    selectedSite,
    handleCreatePage,
    pageGroups,
    handelCreatePagePopUp,
    openCreateNewPage,
    setOpenCreateNewPage,
    oauthTokenHeader,
    duplicatePageResponse,
    setDuplicatePageResponse,
    deletePageData,
    setDeletePageData,
    setSelectedOptionGroup, // Selected option for group
    setSelectedOptionEdit, // Selected option for edit
    selectedOptionGroup, // Selected option for group
    selectedOptionEdit, // Selected option for edit
    isMetaUpdated,
    setIsMetaUpdated,
    pageGroupApiCall,
    pageGroupForAssign,
    setSelectedPageForAssign,
    setPageDetails,
    pageDetails,
    assignPageGroup,
    response,
  };

  const ErrorPopupStatusProps = {
    pageUid,
    setPageStructureDesign,
    setPageDictionary,
    setIsLoading,
    setPageContent,
    oauthTokenHeader,
    selectedSite,
    trackPromise,
    siteUid,
    errorAccToPageStatus,
    setErrorAccToPageStatus,
    setRetry,
    setOpen,
    open,
  };

  return (
    <>
      {isLoading && <SimpleBackdrop />}
      {errorAccToPageStatusLength && (
        <ErrorPopupAccToPageStatus {...ErrorPopupStatusProps} />
      )}
      {sitePages && Object.values(sitePages)?.length > 0 && (
        <SitePages {...sitePagesProps} />
      )}
    </>
  );
}
